import React, { useState } from 'react'
import { PageLayout } from '../components/common/PageLayout'
import { Section, FeatureCard, StatCard, Button } from '../components/common/DesignComponents'

export function PricingPage(): React.ReactElement {
  const [selectedPlan, setSelectedPlan] = useState<string | null>(null)

  const handleSelectPlan = (planName: string) => {
    setSelectedPlan(planName)
    console.log('Selected plan:', planName)
    // In a real app, this would redirect to checkout or show a modal
    setTimeout(() => setSelectedPlan(null), 2000)
  }
  return (
    <PageLayout title="Pricing" subtitle="Choose the perfect plan for your family">
      <Section title="Simple, Transparent Pricing">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {/* Free Plan */}
          <div className="p-8 bg-gradient-to-br from-blue-50 to-cyan-50 rounded-3xl border-4 border-blue-200 shadow-lg">
            <h3 className="text-2xl font-black text-blue-700 mb-2">Free Plan</h3>
            <div className="text-4xl font-black text-blue-600 mb-4">$0<span className="text-lg">/month</span></div>
            <p className="text-gray-700 font-bold mb-6">Perfect for trying out BabyKingdom</p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">1 child profile</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Basic games</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Limited coins</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✗</span>
                <span className="text-gray-500 font-bold">Ad-free experience</span>
              </li>
            </ul>
            <Button variant="outline" onClick={() => handleSelectPlan('Free')} className="w-full">{selectedPlan === 'Free' ? '✅ Selected!' : 'Get Started Free'}</Button>
          </div>

          {/* Premium Plan */}
          <div className="p-8 bg-gradient-to-br from-pink-100 to-purple-100 rounded-3xl border-4 border-pink-400 shadow-xl scale-105">
            <div className="bg-gradient-to-r from-pink-400 to-purple-400 text-white px-4 py-2 rounded-full inline-block font-black mb-4">
              Most Popular ⭐
            </div>
            <h3 className="text-2xl font-black text-purple-700 mb-2">Premium Plan</h3>
            <div className="text-4xl font-black text-purple-600 mb-4">$9.99<span className="text-lg">/month</span></div>
            <p className="text-gray-700 font-bold mb-6">Everything in Free, plus:</p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Up to 5 child profiles</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">All games & features</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Unlimited coins</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Ad-free experience</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Priority support</span>
              </li>
            </ul>
            <Button onClick={() => handleSelectPlan('Premium')} className="w-full">{selectedPlan === 'Premium' ? '✅ Selected!' : 'Start Premium'}</Button>
          </div>

          {/* Family Plan */}
          <div className="p-8 bg-gradient-to-br from-green-50 to-emerald-50 rounded-3xl border-4 border-green-200 shadow-lg">
            <h3 className="text-2xl font-black text-green-700 mb-2">Family Plan</h3>
            <div className="text-4xl font-black text-green-600 mb-4">$24.99<span className="text-lg">/month</span></div>
            <p className="text-gray-700 font-bold mb-6">For larger families</p>
            <ul className="space-y-3 mb-8">
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Unlimited child profiles</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">All Premium features</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Family reporting</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">24/7 support</span>
              </li>
              <li className="flex items-center gap-2">
                <span className="text-2xl">✓</span>
                <span className="text-gray-700 font-bold">Custom themes</span>
              </li>
            </ul>
            <Button variant="secondary" onClick={() => handleSelectPlan('Family')} className="w-full">{selectedPlan === 'Family' ? '✅ Selected!' : 'Choose Family'}</Button>
          </div>
        </div>
      </Section>

      <Section title="Frequently Asked Questions">
        <div className="space-y-6">
          <div className="bg-white p-6 rounded-2xl border-4 border-purple-200">
            <h4 className="font-black text-lg text-purple-700 mb-3">Can I cancel anytime?</h4>
            <p className="text-gray-700">Yes! You can cancel your subscription at any time with no penalties or hidden fees.</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border-4 border-purple-200">
            <h4 className="font-black text-lg text-purple-700 mb-3">Is there a free trial?</h4>
            <p className="text-gray-700">Absolutely! Premium users get a 7-day free trial before any charges apply.</p>
          </div>
          <div className="bg-white p-6 rounded-2xl border-4 border-purple-200">
            <h4 className="font-black text-lg text-purple-700 mb-3">What payment methods do you accept?</h4>
            <p className="text-gray-700">We accept all major credit cards, PayPal, and Apple/Google Pay.</p>
          </div>
        </div>
      </Section>
    </PageLayout>
  )
}
